import { useCallback, useEffect, useState } from 'react'
import QRCode from 'qrcode'
import { Icon } from '@iconify/react'
import { TotpCodeField } from '@/components/TotpCodeField'
import { useAuth } from '@/hooks/useAuth'
import { useInstitution } from '@/contexts/InstitutionContext'
import { ApiClientError } from '@/lib/api/client'
import {
  confirmTotpDevice,
  listTotpDevices,
  revokeTotpDevice,
  startTotpDevice,
} from '@/lib/api/two-factor.service'
import type { TotpDevice, TotpSetupResponse } from '@/types/two-factor'
import styles from './PerfilPage.module.css'

function formatApiError(error: unknown, fallback: string): string {
  if (error instanceof ApiClientError) {
    if (error.errors && Object.keys(error.errors).length > 0) {
      return Object.values(error.errors).flat().join(' ') || fallback
    }
    return error.message || fallback
  }
  return fallback
}

function formatDate(value: string | null): string {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleString('pt-BR')
}

export function TwoFactorSection() {
  const { guard, token } = useAuth()
  const { institutionId } = useInstitution()
  const [devices, setDevices] = useState<TotpDevice[]>([])
  const [isEnabled, setIsEnabled] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [deviceName, setDeviceName] = useState('')
  const [setupPassword, setSetupPassword] = useState('')
  const [setupCode, setSetupCode] = useState('')
  const [confirmCode, setConfirmCode] = useState('')
  const [maxConfirmedDevices, setMaxConfirmedDevices] = useState<number | null>(null)
  const [pendingSetup, setPendingSetup] = useState<
    (TotpSetupResponse & { qrDataUrl: string }) | null
  >(null)
  const [isStarting, setIsStarting] = useState(false)
  const [isConfirming, setIsConfirming] = useState(false)
  const [revokingId, setRevokingId] = useState<number | null>(null)
  const [revokePassword, setRevokePassword] = useState('')
  const [revokeCode, setRevokeCode] = useState('')
  const [isRevoking, setIsRevoking] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)

  const canManage = guard === 'admin' || guard === 'institution'
  const confirmedCount = devices.filter((device) => device.confirmed).length
  const deviceLimit = maxConfirmedDevices ?? (guard === 'admin' ? 3 : 8)
  const atConfirmedLimit = confirmedCount >= deviceLimit
  const requiresTotpToMutate = guard === 'admin' && isEnabled

  const loadDevices = useCallback(async () => {
    if (!token || !guard || (guard !== 'admin' && guard !== 'institution')) {
      setIsLoading(false)
      return
    }

    setIsLoading(true)
    setLoadError(null)
    try {
      const response = await listTotpDevices({ guard })
      setDevices(response.devices)
      setIsEnabled(response.two_factor_enabled)
      setMaxConfirmedDevices(response.max_confirmed_devices)
    } catch (error) {
      setLoadError(formatApiError(error, 'Não foi possível carregar os dispositivos.'))
    } finally {
      setIsLoading(false)
    }
  }, [guard, token])

  useEffect(() => {
    void loadDevices()
  }, [loadDevices])

  async function handleStartSetup(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!guard || (guard !== 'admin' && guard !== 'institution')) return

    setErrorMessage(null)
    setSuccessMessage(null)
    setIsStarting(true)
    try {
      const setup = await startTotpDevice(
        {
          password: setupPassword,
          name: deviceName,
          code: requiresTotpToMutate ? setupCode : undefined,
        },
        { guard }
      )
      const qrDataUrl = await QRCode.toDataURL(setup.otpauth_url, {
        errorCorrectionLevel: 'M',
        margin: 1,
        width: 220,
        color: { dark: '#1a1033', light: '#ffffff' },
      })
      setPendingSetup({ ...setup, qrDataUrl })
      setSetupPassword('')
      setSetupCode('')
      setConfirmCode('')
    } catch (error) {
      setErrorMessage(formatApiError(error, 'Não foi possível iniciar o cadastro.'))
    } finally {
      setIsStarting(false)
    }
  }

  async function handleConfirm(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!guard || !pendingSetup) return

    setErrorMessage(null)
    setSuccessMessage(null)
    setIsConfirming(true)
    try {
      await confirmTotpDevice(pendingSetup.device.id, confirmCode, { guard })
      setPendingSetup(null)
      setConfirmCode('')
      setDeviceName('')
      setSuccessMessage('Autenticação em duas etapas ativada neste dispositivo.')
      await loadDevices()
    } catch (error) {
      setErrorMessage(formatApiError(error, 'Código inválido. Tente novamente.'))
    } finally {
      setIsConfirming(false)
    }
  }

  async function handleRevoke(
    event: React.FormEvent<HTMLFormElement>,
    device: TotpDevice
  ) {
    event.preventDefault()
    if (!guard) return

    setErrorMessage(null)
    setSuccessMessage(null)
    setIsRevoking(true)
    try {
      await revokeTotpDevice(
        device.id,
        {
          password: revokePassword,
          code: device.confirmed || requiresTotpToMutate ? revokeCode : undefined,
        },
        { guard }
      )
      setRevokingId(null)
      setRevokePassword('')
      setRevokeCode('')
      setSuccessMessage('Dispositivo autenticador removido.')
      await loadDevices()
    } catch (error) {
      setErrorMessage(formatApiError(error, 'Não foi possível remover o dispositivo.'))
    } finally {
      setIsRevoking(false)
    }
  }

  if (!canManage) {
    return null
  }

  return (
    <section className={styles.section}>
      <h2 className={styles.sectionTitle}>Autenticação em duas etapas</h2>
      <p className={styles.sectionDescription}>
        Use o Google Authenticator (ou outro app TOTP) para exigir um código a cada login.
        Você pode cadastrar mais de um dispositivo. O 2FA só é ativado depois que o código
        for confirmado
        {guard === 'admin' ? '. Contas administrativas podem ter até 3 autenticadores.' : '.'}
      </p>

      {guard === 'admin' && institutionId !== null && (
        <p className={styles.sectionDescription}>
          Esta configuração vale para a sua conta de administrador, não para a instituição
          selecionada.
        </p>
      )}

      {isLoading && <p className={styles.loadingOverlay}>Carregando dispositivos...</p>}
      {loadError && (
        <div className={styles.errorBanner} role="alert">
          {loadError}
        </div>
      )}

      {!isLoading && (
        <div className={styles.twoFactorStatus} data-enabled={isEnabled}>
          <Icon icon={isEnabled ? 'mdi:shield-check' : 'mdi:shield-alert-outline'} />
          <span>
            {isEnabled
              ? '2FA ativo. O login sempre pedirá um código.'
              : '2FA desativado. Cadastre um autenticador para proteger a conta.'}
          </span>
        </div>
      )}

      {devices.length > 0 && (
        <ul className={styles.deviceList}>
          {devices.map((device) => (
            <li key={device.id} className={styles.deviceCard}>
              <div className={styles.deviceInfo}>
                <strong>{device.name}</strong>
                <span>
                  {device.confirmed ? 'Ativo' : 'Aguardando confirmação'} · último uso:{' '}
                  {formatDate(device.last_used_at)}
                </span>
              </div>
              {revokingId === device.id ? (
                <form
                  className={styles.revokeForm}
                  onSubmit={(event) => {
                    void handleRevoke(event, device)
                  }}
                >
                  <input
                    type="password"
                    className={styles.input}
                    placeholder="Senha atual"
                    autoComplete="current-password"
                    value={revokePassword}
                    onChange={(event) => setRevokePassword(event.target.value)}
                    disabled={isRevoking}
                    required
                  />
                  {(device.confirmed || requiresTotpToMutate) && (
                    <TotpCodeField
                      id={`revoke-code-${device.id}`}
                      value={revokeCode}
                      onChange={setRevokeCode}
                      disabled={isRevoking}
                      label="Código para confirmar a remoção"
                    />
                  )}
                  <div className={styles.deviceActions}>
                    <button
                      type="submit"
                      className={styles.dangerButton}
                      disabled={
                        isRevoking
                        || ((device.confirmed || requiresTotpToMutate) && revokeCode.length !== 6)
                      }
                    >
                      {isRevoking ? 'Removendo...' : 'Confirmar remoção'}
                    </button>
                    <button
                      type="button"
                      className={styles.secondaryButton}
                      disabled={isRevoking}
                      onClick={() => {
                        setRevokingId(null)
                        setRevokePassword('')
                        setRevokeCode('')
                      }}
                    >
                      Cancelar
                    </button>
                  </div>
                </form>
              ) : (
                <button
                  type="button"
                  className={styles.secondaryButton}
                  onClick={() => {
                    setRevokingId(device.id)
                    setErrorMessage(null)
                    setSuccessMessage(null)
                  }}
                >
                  Remover
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {pendingSetup ? (
        <form className={styles.setupPanel} onSubmit={handleConfirm}>
          <h3 className={styles.setupTitle}>Confirme este dispositivo</h3>
          <p className={styles.sectionDescription}>
            Escaneie o QR Code no Google Authenticator e digite o código gerado para
            ativar. Sem essa confirmação, o 2FA não entra em vigor.
          </p>
          <div className={styles.qrWrap}>
            <img src={pendingSetup.qrDataUrl} alt="QR Code do autenticador" />
            <div className={styles.secretBox}>
              <span>Chave manual</span>
              <code>{pendingSetup.secret}</code>
            </div>
          </div>
          <TotpCodeField
            id="confirm-totp-code"
            value={confirmCode}
            onChange={setConfirmCode}
            disabled={isConfirming}
            autoFocus
          />
          <div className={styles.deviceActions}>
            <button
              type="submit"
              className={styles.submit}
              disabled={isConfirming || confirmCode.length !== 6}
            >
              {isConfirming ? 'Confirmando...' : 'Ativar dispositivo'}
            </button>
            <button
              type="button"
              className={styles.secondaryButton}
              disabled={isConfirming}
              onClick={() => {
                setPendingSetup(null)
                setConfirmCode('')
              }}
            >
              Cancelar cadastro
            </button>
          </div>
        </form>
      ) : atConfirmedLimit ? (
        <p className={styles.sectionDescription} role="status">
          Limite de {deviceLimit} dispositivos atingido.
        </p>
      ) : (
        <form className={styles.fields} onSubmit={handleStartSetup}>
          <div className={styles.field}>
            <label className={styles.label} htmlFor="totp-device-name">
              Nome do dispositivo
            </label>
            <input
              id="totp-device-name"
              type="text"
              className={styles.input}
              placeholder="Ex.: iPhone, Google Authenticator"
              maxLength={80}
              value={deviceName}
              onChange={(event) => setDeviceName(event.target.value)}
              disabled={isStarting}
            />
          </div>
          <div className={styles.field}>
            <label className={styles.label} htmlFor="totp-setup-password">
              Senha atual
            </label>
            <input
              id="totp-setup-password"
              type="password"
              className={styles.input}
              placeholder="Confirme sua senha para adicionar"
              autoComplete="current-password"
              value={setupPassword}
              onChange={(event) => setSetupPassword(event.target.value)}
              disabled={isStarting}
              required
            />
          </div>
          {requiresTotpToMutate ? (
            <TotpCodeField
              id="setup-totp-code"
              value={setupCode}
              onChange={setSetupCode}
              disabled={isStarting}
              label="Código de um autenticador já cadastrado"
            />
          ) : null}
          <button
            type="submit"
            className={styles.secondaryButton}
            disabled={isStarting || (requiresTotpToMutate && setupCode.length !== 6)}
          >
            {isStarting ? 'Gerando QR Code...' : 'Adicionar autenticador'}
          </button>
        </form>
      )}

      {errorMessage && (
        <div className={styles.errorBanner} role="alert">
          {errorMessage}
        </div>
      )}
      {successMessage && (
        <div className={styles.successBanner} role="status">
          {successMessage}
        </div>
      )}
    </section>
  )
}
